import type { ExperienceFooter, FooterColumn, ExperienceLink } from "@/lib/experience-definition/types";
import { footerColumns } from "@/lib/experience-definition/presets/helpers";

export type FooterCategoryLink = {
  name: string;
  slug: string;
};

const MAX_EXPLORE_LINKS = 8;

function isExploreColumn(col: FooterColumn): boolean {
  const id = (col.id ?? "").toLowerCase();
  const title = (col.title ?? "").toLowerCase();
  return id === "explore" || title === "explorar";
}

function isShopColumn(col: FooterColumn): boolean {
  const id = (col.id ?? "").toLowerCase();
  return id === "shop" || id === "tienda";
}

/** Links de Explorar a partir de categorías del tenant. */
export function exploreLinksFromCategories(
  categories: FooterCategoryLink[],
): Array<{ label: string; href?: string; page?: ExperienceLink["page"] }> {
  const live = categories
    .filter((c) => c.name.trim() && c.slug.trim())
    .slice(0, MAX_EXPLORE_LINKS)
    .map((c) => ({
      label: c.name.trim(),
      href: `/categoria/${c.slug.trim()}`,
    }));
  if (live.length) return live;
  return [{ label: "Catálogo", page: "plp" as const }];
}

/**
 * Reescribe columnas del footer al sembrar experiencia:
 * - Columna shop → nombre real de la tienda
 * - Columna Explorar → categorías del tenant (o Catálogo)
 * - Ayuda se mantiene
 */
export function sanitizeExperienceFooterColumns(opts: {
  tenantName: string;
  categories?: FooterCategoryLink[];
  existing?: FooterColumn[] | null;
}): FooterColumn[] {
  const name = opts.tenantName.trim() || "Tienda";
  const explore = exploreLinksFromCategories(opts.categories ?? []);
  const base = footerColumns(name, explore);

  const existing = opts.existing?.filter((c) => c.links?.length) ?? [];
  if (!existing.length) return base;

  // Conservar columnas custom del merchant que no sean shop/explore/help preset.
  const help = base.find((c) => c.id === "help");
  const shop = base.find((c) => c.id === "shop")!;
  const exploreCol = base.find((c) => c.id === "explore")!;

  const extras = existing.filter((c) => {
    if (isShopColumn(c) || isExploreColumn(c)) return false;
    const id = (c.id ?? "").toLowerCase();
    const title = (c.title ?? "").toLowerCase();
    if (id === "help" || title === "ayuda") return false;
    return true;
  });

  return [shop, exploreCol, ...extras, ...(help ? [help] : [])];
}

/** Aplica sanitize sobre un footer completo (nuevo objeto). */
export function withDynamicFooterColumns(
  footer: ExperienceFooter | undefined,
  opts: { tenantName: string; categories?: FooterCategoryLink[] },
): ExperienceFooter | undefined {
  if (!footer) return footer;
  return {
    ...footer,
    columns: sanitizeExperienceFooterColumns({
      tenantName: opts.tenantName,
      categories: opts.categories,
      existing: footer.columns,
    }),
  };
}
